// src/utils/caretPreserve.js
//
// Caret preservation across DOM decoration passes.
//
// Any decoration that wraps text in spans destroys the text nodes a live
// Range points at, so a caret inside the decorated block would be lost. These
// two helpers record the caret as a character offset into a block's text — a
// position that survives re-nodding — and put it back afterwards.
//
// A <br> counts as one character, in both directions. "The end of a line" and
// "the start of the next" are the same text offset, and only the <br> between
// them tells them apart: without it, a caret on a fresh soft line (Shift+Enter)
// was put back at the end of the line above.
//
// Extracted from collabHighlights.js so commentHighlights.js can use the same
// technique instead of the cruder alternative of refusing to decorate the
// block the caret happens to be in.

const indexOf = (node) => Array.prototype.indexOf.call(node.parentNode.childNodes, node);

function place(container, offset) {
  const sel = window.getSelection();
  const range = document.createRange();
  range.setStart(container, offset);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Record the collapsed caret's position as a character offset into `block`.
 * Returns null when there's no collapsed caret inside the block — the caller
 * should then skip restoring rather than move the user's cursor.
 * @param {Element} block
 * @returns {number|null}
 */
export function caretOffsetIn(block) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
  const r = sel.getRangeAt(0);
  if (!block.contains(r.startContainer)) return null;
  // Measured as the content between the block's start and the caret, rather
  // than by finding the caret's text node, because the caret is not always in
  // one: after typing, the editor routinely leaves it as (p, 1) — a position
  // between the paragraph's children. Searching for a text node returned null
  // there, nothing was restored, and decorating the paragraph sent the caret
  // to its start.
  const before = document.createRange();
  before.selectNodeContents(block);
  before.setEnd(r.startContainer, r.startOffset);
  const breaks = Array.from(block.querySelectorAll('br'))
    .filter((br) => before.intersectsNode(br)).length;
  return before.toString().length + breaks;
}

/**
 * Restore a collapsed caret to `targetOffset` characters into `block`'s text,
 * counting each <br> as one. No-op if the block has nothing to place it in.
 * Only call when the caret was previously inside `block` (so we never steal
 * focus from elsewhere).
 * @param {Element} block
 * @param {number|null} targetOffset
 */
export function restoreCaretIn(block, targetOffset) {
  if (targetOffset == null) return;
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) => (n.nodeType === 3 || n.tagName === 'BR'
      ? NodeFilter.FILTER_ACCEPT
      : NodeFilter.FILTER_SKIP),
  });
  let remaining = targetOffset;
  let end = null; // the position just past the last node walked
  let node;
  while ((node = walker.nextNode())) {
    if (node.nodeType === 1) {
      // A <br>. With nothing left to count, the caret sits just before it.
      if (remaining === 0) { place(node.parentNode, indexOf(node)); return; }
      remaining -= 1;
      end = [node.parentNode, indexOf(node) + 1];
    } else {
      const len = node.textContent.length;
      if (remaining <= len) { place(node, remaining); return; }
      remaining -= len;
      end = [node, len];
    }
  }
  // Past the end — drop the caret after the last thing in the block.
  if (end) place(end[0], end[1]);
}
